/**
 * PartnerPortalPage — portal del partner (RES-93, épica RES-88).
 *
 * El partner entra por el login normal con un usuario de rol PARTNER; ProtectedRoute lo manda
 * acá y esto es todo lo que ve (pantalla completa, sin layout del restaurante). Los datos salen
 * de `GET /partners/me`, que calcula con el mismo motor que el panel interno y solo devuelve
 * lo del propio partner.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { getMyPartner } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { TourProvider } from '../context/TourContext';
import HelpIcon from '../components/help/HelpIcon';
import LanguageSelector from '../components/LanguageSelector';
import PartnerLinkCard from '../components/partner-portal/PartnerLinkCard';
import PartnerProgramCard from '../components/partner-portal/PartnerProgramCard';
import PayoutDetailsForm from '../components/partner-portal/PayoutDetailsForm';
import PartnerConversionsTable from '../components/partner-portal/PartnerConversionsTable';
import PartnerPayoutsTable from '../components/partner-portal/PartnerPayoutsTable';
import { formatCurrency } from '../utils/formatCurrency';
import { getDateLocale } from '../utils/dateLocale';
import {
  PAYOUT_THRESHOLD,
  formatPeriod,
  formatShortDate,
  isNotAPartnerError,
} from '../utils/partnerPortal';
import './PartnerPortalPage.css';

function StatCard({ id, label, value, note }) {
  return (
    <div className="pp-stat" data-testid={`pp-stat-${id}`}>
      <span className="pp-stat-label">{label}</span>
      <strong className="pp-stat-value">{value}</strong>
      {note && <span className="pp-stat-note">{note}</span>}
    </div>
  );
}

function PartnerPortalContent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const locale = getDateLocale((i18n?.language || 'es').split('-')[0]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // 'notPartner' | 'generic' | null

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMyPartner();
      setData(response);
    } catch (err) {
      console.error('[PartnerPortalPage] Error cargando el partner:', err);
      setError(isNotAPartnerError(err) ? 'notPartner' : 'generic');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handlePayoutSaved = (payoutDetails) => {
    if (!payoutDetails) return;
    setData((prev) => (prev ? { ...prev, partner: { ...prev.partner, payoutDetails } } : prev));
  };

  const partner = data?.partner;
  const metrics = data?.metrics || {};

  const header = (
    <header className="pp-header">
      <div className="pp-brand">
        <span className="pp-brand-name">Resto</span>
        <span className="pp-brand-sub">{t('partnerPortal.header.subtitle')}</span>
      </div>
      <div className="pp-header-actions">
        {partner && (
          <span className="pp-header-name" data-testid="pp-header-name">
            {partner.partnerName}
          </span>
        )}
        <LanguageSelector />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleLogout}
          data-testid="pp-logout"
        >
          <LogOut size={16} aria-hidden="true" /> {t('partnerPortal.header.logout')}
        </button>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="pp-page">
        {header}
        <main className="pp-main">
          <p className="pp-loading">{t('common.loading')}</p>
        </main>
      </div>
    );
  }

  if (error || !partner) {
    const kind = error || 'generic';
    return (
      <div className="pp-page">
        {header}
        <main className="pp-main">
          <section className="pp-card pp-error-card" role="alert" data-testid={`pp-error-${kind}`}>
            <h1>{t(`partnerPortal.errors.${kind}Title`)}</h1>
            <p>{t(`partnerPortal.errors.${kind}`)}</p>
            <div className="pp-form-actions">
              {kind === 'generic' && (
                <button type="button" className="btn btn-primary" onClick={load}>
                  {t('partnerPortal.errors.retry')}
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleLogout}
                data-testid="pp-error-logout"
              >
                {t('partnerPortal.header.logout')}
              </button>
            </div>
          </section>
        </main>
      </div>
    );
  }

  const nextPayout = metrics.nextPayout;
  const cancelledTotal = (metrics.cancelled || 0) + (metrics.clawedBack || 0);

  return (
    <div className="pp-page">
      {header}
      <main className="pp-main">
        <div className="pp-hero">
          <h1 className="pp-title">
            {t('partnerPortal.title', { name: partner.partnerName })}{' '}
            <HelpIcon topicKey="partners.portal" />
          </h1>
          <p className="pp-subtitle">
            <span className="pp-badge pp-badge--program" data-testid="pp-program-badge">
              {t(`partnerPortal.programs.${partner.program}`)}
            </span>
            {partner.status && partner.status !== 'active' && (
              <span className="pp-badge pp-badge--warning">
                {t(`partnerPortal.status.${partner.status}`)}
              </span>
            )}
            {metrics.lastCalculatedPeriod && (
              <span className="pp-muted">
                {t('partnerPortal.lastCalculated', {
                  period: formatPeriod(metrics.lastCalculatedPeriod),
                })}
              </span>
            )}
          </p>
        </div>

        <PartnerLinkCard landingUrl={partner.landingUrl} code={partner.code} />

        <div className="pp-stats">
          <StatCard
            id="trials"
            label={t('partnerPortal.stats.trials')}
            value={metrics.trials || 0}
          />
          <StatCard
            id="paying"
            label={t('partnerPortal.stats.paying')}
            value={metrics.activeConversions || 0}
            note={
              metrics.resaleLocations > 0
                ? t('partnerPortal.stats.resaleNote', { count: metrics.resaleLocations })
                : null
            }
          />
          <StatCard
            id="cancelled"
            label={t('partnerPortal.stats.cancelled')}
            value={cancelledTotal}
            note={
              metrics.clawedBack > 0
                ? t('partnerPortal.stats.clawedBackNote', { count: metrics.clawedBack })
                : null
            }
          />
          <StatCard
            id="mrr"
            label={t('partnerPortal.stats.mrr')}
            value={formatCurrency(metrics.mrr)}
          />
          <StatCard
            id="accrued"
            label={t('partnerPortal.stats.accrued')}
            value={formatCurrency(metrics.commissionAccrued)}
            note={t('partnerPortal.stats.paidNote', {
              amount: formatCurrency(metrics.commissionPaid),
            })}
          />
          <StatCard
            id="next-payout"
            label={t('partnerPortal.stats.nextPayout')}
            value={nextPayout ? formatCurrency(nextPayout.amount) : '—'}
            note={
              nextPayout
                ? t('partnerPortal.stats.nextPayoutNote', {
                    period: formatPeriod(nextPayout.period),
                    date: formatShortDate(nextPayout.estimatedDate, locale) || '—',
                  })
                : t('partnerPortal.stats.noNextPayout')
            }
          />
        </div>

        {metrics.pendingRollover > 0 && (
          <div className="pp-rollover" role="status" data-testid="pp-rollover">
            {t('partnerPortal.rollover', {
              amount: formatCurrency(metrics.pendingRollover),
              threshold: formatCurrency(PAYOUT_THRESHOLD),
            })}
          </div>
        )}

        <div className="pp-columns">
          <PartnerProgramCard partner={partner} metrics={metrics} cmTiers={data.cmTiers || []} />
          <PayoutDetailsForm payoutDetails={partner.payoutDetails} onSaved={handlePayoutSaved} />
        </div>

        <PartnerConversionsTable
          conversions={metrics.conversions || []}
          program={partner.program}
          locale={locale}
        />
        <PartnerPayoutsTable payouts={metrics.payouts || []} locale={locale} />
      </main>
    </div>
  );
}

function PartnerPortalPage() {
  // Fuera de AppLayout no hay TourProvider y HelpIcon lo necesita (mismo patrón que el paywall).
  return (
    <TourProvider>
      <PartnerPortalContent />
    </TourProvider>
  );
}

export default PartnerPortalPage;
